import React, { useState, useMemo, useEffect, useReducer } from 'react';
import { Package, User, Clock, Search, Filter, TrendingUp, Archive, Trash2, ArrowDownToLine } from 'lucide-react';
import MobileModal from './MobileModal';
import { useCharacter } from '../contexts/CharacterContext';
import { useUI } from '../contexts/UIContext';
import { systemBus, EVENTS } from '../services/systemBus';
import { useConfirm } from '../contexts/ConfirmContext';

/**
 * SharedVaultModal - Party Loot Distribution System
 *
 * Allows party members to:
 * - Add items from inventory to shared vault
 * - Take items from vault to personal inventory
 * - See who added items and when
 * - Filter/search vault contents
 * - Real-time sync across all party members
 *
 * [PURPOSE]: Collaborative loot management for D&D parties
 * [WHY]: Players need a way to share treasure, distribute loot fairly
 * [INTEGRATION]: Connects to useEnhancedPartySync for Firebase sync
 * [ARCHITECTURE]: Uses context hooks following Phase 4B migration pattern
 */
const SharedVaultModal = ({
  isOpen,
  onClose,
  vaultItems = {}, // From useEnhancedPartySync (not in context yet, defer to future phase)
  // [PHASE 4B - PARTIAL MIGRATION]: Keeping function props for Phase 4B Step 6
  onAddItem, // addToSharedVault(item)
  onTakeItem, // takeFromSharedVault(itemId)
  onRemoveItem, // removeFromSharedVault(itemId) - DM only
  isDM = false
}) => {
  // [UI-MOBILEMODAL-ADOPTION wave 2c-2 2026-07-09]: Escape dismissal now comes
  // from MobileModal's own internal useModalEscape registration — this file no
  // longer calls the hook itself (see the migrated return below).

  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  const { playerStats } = useCharacter();
  const { showStatus } = useUI();
  const confirm = useConfirm();
  const currentCharacterId = playerStats.id || null;
  const currentCharacterName = playerStats.name || 'Unknown';
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBy, setFilterBy] = useState('all'); // all, weapons, armor, consumables, misc
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, value, name, rarity
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  // [PHASE 5 SESSION 4]: Force re-render mechanism for reactive shared vault
  // [PURPOSE]: Enables modal to react to vault item changes from party members
  // [WHY]: When party members add/take items, vault display needs immediate updates
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [PHASE 5 SESSION 4]: Event subscriptions for reactive shared vault modal
  // [PURPOSE]: Subscribe to vault and party events for real-time party loot management
  // [WHY]: This modal needs to show real-time updates when:
  //   - Party members add items to shared vault
  //   - Items are taken from shared vault
  //   - Vault is shared with new party members
  //   - Party members join/leave (affects who can access vault)
  // [INTEGRATION]: Provides collaborative loot distribution for D&D parties
  useEffect(() => {
    const handleSharedVaultEvent = (eventName, data) => {
      // Force re-render to show updated vault contents
      forceUpdate();
    };

    // [VAULT_SHARED]: Vault was shared with party members
    const unsubscribeShared = systemBus.on(EVENTS.VAULT_SHARED, (data) =>
      handleSharedVaultEvent('VAULT_SHARED', data));

    // [INVENTORY_VAULT_TRANSFER]: Items transferred to/from vault
    const unsubscribeTransfer = systemBus.on(EVENTS.INVENTORY_VAULT_TRANSFER, (data) =>
      handleSharedVaultEvent('INVENTORY_VAULT_TRANSFER', data));

    // [PARTY_MEMBER_ADDED]: New party member joined (may need vault access)
    const unsubscribeMemberAdded = systemBus.on(EVENTS.PARTY_MEMBER_ADDED, (data) =>
      handleSharedVaultEvent('PARTY_MEMBER_ADDED', data));

    // [PARTY_MEMBER_UPDATED]: Party member updated (permissions may change)
    const unsubscribeMemberUpdated = systemBus.on(EVENTS.PARTY_MEMBER_UPDATED, (data) =>
      handleSharedVaultEvent('PARTY_MEMBER_UPDATED', data));

    return () => {
      unsubscribeShared();
      unsubscribeTransfer();
      unsubscribeMemberAdded();
      unsubscribeMemberUpdated();
    };
  }, []);

  // [PHASE 6A]: Function to emit vault sharing event
  // PURPOSE: Notify system when vault is shared with party members
  // WHY: Enables party-wide vault access notifications and sync
  // NOTE: This should be called when vault sharing is initiated (e.g., from DM tools or vault creation)
  const emitVaultShared = (vaultId, partyMembers, permissions = 'full') => {
    systemBus.emit(EVENTS.VAULT_SHARED, {
      vault: { id: vaultId, items: vaultItems },
      vaultId,
      partyMembers: partyMembers || [],
      permissions // 'view', 'deposit', 'withdraw', 'full'
    });
  };

  // [PHASE 6A]: Emit vault shared event when modal opens (if not already shared)
  // This ensures the vault sharing is tracked when first accessed
  useEffect(() => {
    if (isOpen && !window.__vaultSharedEmitted) {
      // Emit sharing event on first open (prevents duplicate emissions)
      emitVaultShared('shared_vault_main', [], 'full');
      window.__vaultSharedEmitted = true;
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convert vaultItems object to array
  const vaultItemsArray = useMemo(() => {
    return Object.entries(vaultItems).map(([id, data]) => ({
      id,
      ...data.item,
      addedBy: data.addedBy,
      addedByName: data.addedByName,
      addedAt: data.addedAt,
      claimedBy: data.claimedBy,
      claimedByName: data.claimedByName
    }));
  }, [vaultItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = [...vaultItemsArray];

    // Search filter
    if (searchQuery) {
      items = items.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.addedByName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter
    if (filterBy !== 'all') {
      items = items.filter(item => {
        const type = item.type?.toLowerCase();
        if (filterBy === 'weapons') return type === 'weapon';
        if (filterBy === 'armor') return type === 'armor';
        if (filterBy === 'consumables') return type === 'consumable' || type === 'potion';
        if (filterBy === 'misc') return !['weapon', 'armor', 'consumable', 'potion'].includes(type);
        return true;
      });
    }

    // Sort
    items.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.addedAt) - new Date(a.addedAt);
      if (sortBy === 'oldest') return new Date(a.addedAt) - new Date(b.addedAt);
      if (sortBy === 'value') return (b.value || 0) - (a.value || 0);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'rarity') {
        const rarityOrder = { common: 0, uncommon: 1, rare: 2, 'very rare': 3, legendary: 4, artifact: 5 };
        return (rarityOrder[b.rarity?.toLowerCase()] || 0) - (rarityOrder[a.rarity?.toLowerCase()] || 0);
      }
      return 0;
    });

    return items;
  }, [vaultItemsArray, searchQuery, filterBy, sortBy]);

  // Rarity color helper
  const getRarityColor = (rarity) => {
    const r = rarity?.toLowerCase();
    if (r === 'common') return 'text-gray-400 border-gray-500';
    if (r === 'uncommon') return 'text-green-400 border-green-500';
    if (r === 'rare') return 'text-blue-400 border-blue-500';
    if (r === 'very rare') return 'text-purple-400 border-purple-500';
    if (r === 'legendary') return 'text-yellow-400 border-yellow-500';
    if (r === 'artifact') return 'text-red-400 border-red-500';
    return 'text-gray-400 border-gray-500';
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  // [UI-MOBILEMODAL-ADOPTION wave 2c-2 2026-07-09]: Migrated onto the shared
  // MobileModal shell (MOBILEMODAL-WAVE2C2-GRADIENT). Identity chrome:
  // - headerClassName carries the green gradient + border (was
  //   'bg-gradient-to-r from-green-900 to-green-800 ... border-b-2
  //   border-green-500'; structural p-6/flex + rounded-t-lg dropped — corner
  //   rounding now comes free from the card's overflow-hidden).
  // - className carries the card-level green border (border-2
  //   border-green-500); the card bg itself falls back to MobileModal's
  //   default bg-gray-800 (was bg-gray-900 — a one-shade lighter, sanctioned
  //   drift since className only appends, it can't safely override a
  //   same-property bg utility the way headerClassName replaces one).
  // - maxWidthClass="max-w-5xl" preserves the original desktop card width.
  // - The title's icon + name + item-count subtitle move into a JSX `title`
  //   (span-based, not h2/p, to avoid nesting headings inside MobileModal's
  //   own <h2> — same technique as FacilityDetailModal's icon title).
  // - The Controls toolbar and Footer Stats bar were full-bleed, pinned
  //   siblings in the old flex-col shell (Content alone scrolled). MobileModal
  //   has a single scrolling body, so both are bled to the card edges via
  //   negative margins (counteracting the body's own p-4/p-6) and `sticky
  //   top-0` / `sticky bottom-0` respectively — mirroring FeatsPanel's
  //   Add-Feat picker toolbar migration (wave 2c) for the top bar, extended
  //   with the same technique for the bottom bar so it stays pinned exactly
  //   as it did in the original flex-col layout.
  // - No onSave/showSaveButton: the original header had only a close button.
  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-3">
          <Package size={28} className="text-green-300 flex-shrink-0" />
          <span className="text-left">
            <span className="block text-lg font-bold text-white leading-tight">Party Shared Vault</span>
            <span className="block text-xs font-normal text-green-200 leading-tight">
              Collaborative loot storage • {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
            </span>
          </span>
        </span>
      }
      ariaLabel="Party Shared Vault"
      showSaveButton={false}
      headerClassName="bg-gradient-to-r from-green-900 to-green-800 border-b-2 border-green-500"
      className="border-2 border-green-500"
      maxWidthClass="max-w-5xl"
    >
      {/* Controls — bled to the card edges (negative margin counters the body's
          own p-4/p-6) and sticky under the header so it stays visible while
          the vault list scrolls, matching the original pinned toolbar. */}
      <div className="sticky top-0 z-10 -mx-4 -mt-4 px-4 pt-4 pb-3 md:-mx-6 md:-mt-6 md:px-6 md:pt-6 mb-4 bg-gray-800 border-b border-gray-700 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search items, types, or who added..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-700 text-white pl-10 pr-4 py-2 rounded border border-gray-600 focus:border-green-500 focus:outline-none"
          />
        </div>

        {/* Filters and Sort */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Filter by type */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 text-sm"
            >
              <option value="all">All Items</option>
              <option value="weapons">Weapons</option>
              <option value="armor">Armor</option>
              <option value="consumables">Consumables</option>
              <option value="misc">Miscellaneous</option>
            </select>
          </div>

          {/* Sort by */}
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-gray-700 text-white px-3 py-1 rounded border border-gray-600 text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="value">Highest Value</option>
              <option value="name">Name (A-Z)</option>
              <option value="rarity">Rarity</option>
            </select>
          </div>

          {/* View mode toggle */}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'grid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm ${
                viewMode === 'list'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-16">
          <Package size={64} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-xl font-bold text-gray-400 mb-2">Vault is Empty</h3>
          <p className="text-gray-500">
            {searchQuery || filterBy !== 'all'
              ? 'No items match your filters'
              : 'Add items from your inventory to share with the party'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => (
            <VaultItemCard
              key={item.id}
              item={item}
              onTake={() => {
                onTakeItem(item.id);
                showStatus(`✅ Took ${item.name} from shared vault`);
              }}
              onRemove={isDM ? async () => {
                if (await confirm({
                  title: 'Remove Vault Item',
                  message: `Remove ${item.name} from vault? This cannot be undone.`,
                  confirmText: 'Remove',
                  confirmColor: 'red',
                })) {
                  onRemoveItem(item.id);
                  showStatus(`🗑️ Removed ${item.name} from vault`);
                }
              } : null}
              getRarityColor={getRarityColor}
              formatDate={formatDate}
              currentCharacterId={currentCharacterId}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredItems.map((item) => (
            <VaultItemRow
              key={item.id}
              item={item}
              onTake={() => {
                onTakeItem(item.id);
                showStatus(`✅ Took ${item.name} from shared vault`);
              }}
              onRemove={isDM ? async () => {
                if (await confirm({
                  title: 'Remove Vault Item',
                  message: `Remove ${item.name} from vault? This cannot be undone.`,
                  confirmText: 'Remove',
                  confirmColor: 'red',
                })) {
                  onRemoveItem(item.id);
                  showStatus(`🗑️ Removed ${item.name} from vault`);
                }
              } : null}
              getRarityColor={getRarityColor}
              formatDate={formatDate}
              currentCharacterId={currentCharacterId}
            />
          ))}
        </div>
      )}

      {/* Footer Stats — bled + sticky at the bottom, mirroring the pinned
          footer bar from the original flex-col shell. */}
      <div className="sticky bottom-0 z-10 -mx-4 -mb-4 px-4 pb-4 pt-4 md:-mx-6 md:-mb-6 md:px-6 md:pb-6 mt-4 bg-gray-800 border-t border-gray-700">
        <div className="flex justify-between items-center text-sm">
          <div className="text-gray-400">
            Total Items: <span className="text-white font-bold">{filteredItems.length}</span>
          </div>
          <div className="text-gray-400">
            Total Value: <span className="text-green-400 font-bold">
              {filteredItems.reduce((sum, item) => sum + (item.value || 0), 0)} gp
            </span>
          </div>
          <div className="text-gray-400">
            <span className="text-yellow-400">💡 Tip:</span> Right-click inventory items to add to vault
          </div>
        </div>
      </div>
    </MobileModal>
  );
};

// Vault Item Card Component (Grid View)
const VaultItemCard = ({ item, onTake, onRemove, getRarityColor, formatDate, currentCharacterId }) => {
  const isClaimed = !!item.claimedBy;
  const isClaimedByMe = item.claimedBy === currentCharacterId;

  return (
    <div className={`bg-gray-800 rounded-lg border-2 ${
      isClaimed ? 'border-gray-700 opacity-75' : getRarityColor(item.rarity)
    } p-4 hover:shadow-lg transition-shadow`}>

      {/* Item Header */}
      <div className="flex justify-between items-start mb-2">
        <h3 className={`font-bold ${getRarityColor(item.rarity).split(' ')[0]}`}>
          {item.name}
        </h3>
        {isClaimed && (
          <span className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-400">
            Claimed
          </span>
        )}
      </div>

      {/* Item Details */}
      <div className="space-y-1 text-sm text-gray-400 mb-3">
        <div className="flex items-center gap-2">
          <Package size={14} />
          <span>{item.type || 'Item'}</span>
          {item.rarity && (
            <span className={`text-xs ${getRarityColor(item.rarity).split(' ')[0]}`}>
              ({item.rarity})
            </span>
          )}
        </div>

        {item.value > 0 && (
          <div className="text-green-400">
            💰 {item.value} gp
          </div>
        )}

        {item.weight > 0 && (
          <div>
            ⚖️ {item.weight} lb
          </div>
        )}
        {item.description && (
          <div className="text-gray-500 text-xs mt-1 line-clamp-2">{item.description}</div>
        )}
      </div>

      {/* Added By Info */}
      <div className="border-t border-gray-700 pt-2 mb-3 text-xs text-gray-500">
        <div className="flex items-center gap-2 mb-1">
          <User size={12} />
          <span>Added by <span className="text-gray-300">{item.addedByName}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={12} />
          <span>{formatDate(item.addedAt)}</span>
        </div>
        {isClaimed && (
          <div className="flex items-center gap-2 mt-1 text-yellow-500">
            <ArrowDownToLine size={12} />
            <span>Claimed by {isClaimedByMe ? 'you' : item.claimedByName}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {!isClaimed && (
          <button
            onClick={onTake}
            className="flex-1 bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded font-bold text-sm flex items-center justify-center gap-2"
          >
            <ArrowDownToLine size={16} />
            Take Item
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded text-sm"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

// Vault Item Row Component (List View)
const VaultItemRow = ({ item, onTake, onRemove, getRarityColor, formatDate, currentCharacterId }) => {
  const isClaimed = !!item.claimedBy;
  const isClaimedByMe = item.claimedBy === currentCharacterId;

  return (
    <div className={`bg-gray-800 rounded border ${
      isClaimed ? 'border-gray-700 opacity-75' : getRarityColor(item.rarity).split(' ')[1]
    } p-3 flex items-center gap-4 hover:bg-gray-700 transition-colors`}>

      {/* Item Name & Type */}
      <div className="flex-1 min-w-0">
        <div className={`font-bold ${getRarityColor(item.rarity).split(' ')[0]} truncate`}>
          {item.name}
        </div>
        <div className="text-xs text-gray-400">
          {item.type || 'Item'} {item.rarity && `• ${item.rarity}`}
        </div>
      </div>

      {/* Value */}
      {item.value > 0 && (
        <div className="text-green-400 font-bold text-sm whitespace-nowrap">
          {item.value} gp
        </div>
      )}

      {/* Added By */}
      <div className="text-xs text-gray-500 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <User size={12} />
          {item.addedByName}
        </div>
        <div className="flex items-center gap-1">
          <Clock size={12} />
          {formatDate(item.addedAt)}
        </div>
      </div>

      {/* Claimed Status */}
      {isClaimed && (
        <div className="text-xs text-yellow-500 whitespace-nowrap">
          Claimed by {isClaimedByMe ? 'you' : item.claimedByName}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {!isClaimed && (
          <button
            onClick={onTake}
            className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded font-bold text-sm flex items-center gap-2"
          >
            <ArrowDownToLine size={14} />
            Take
          </button>
        )}
        {onRemove && (
          <button
            onClick={onRemove}
            className="bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded text-sm"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SharedVaultModal;
